import React, { useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export default function ChangePasswordModal({ isOpen, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      setIsLoading(true);
      await api.post('/auth/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });
      
      toast.success('Password changed successfully!');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      onClose();
    } catch (error) {
      console.error('Change password error:', error);
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-50 border-[#F3F4F6] text-gray-900">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Change Password
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="currentPassword" className="text-gray-600 text-sm mb-2 block">
              Current Password
            </Label>
            <Input
              id="currentPassword"
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
              placeholder="••••••••"
              data-testid="current-password-input"
            />
          </div>
          
          <div>
            <Label htmlFor="newPassword" className="text-gray-600 text-sm mb-2 block">
              New Password
            </Label>
            <Input
              id="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
              placeholder="••••••••"
              data-testid="new-password-input"
            />
            <p className="text-gray-500 text-xs mt-1">Must be at least 6 characters</p>
          </div>
          
          <div>
            <Label htmlFor="confirmPassword" className="text-gray-600 text-sm mb-2 block">
              Confirm New Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
              placeholder="••••••••"
              data-testid="confirm-password-input"
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 px-6 bg-transparent border border-[#F3F4F6] text-gray-900 font-bold uppercase tracking-wider rounded-lg hover:border-[#334155] hover:text-[#334155] transition-all text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 h-10 px-6 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              data-testid="change-password-button"
            >
              {isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
